import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Facebook, Plus, Loader2, Unplug, Stethoscope } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import {
  useZaloAccounts,
  useConnectFacebookPage,
  useDeleteZaloAccount,
  useFacebookConfig,
  useDiagnoseFacebookPage,
  PLATFORM,
  type FbConfigStatus,
  type FbDiagnosis,
} from '@/hooks/use-integrations'
import {
  SelectableTable,
  accountColumns,
  accountSearchText,
  ACCOUNT_FILTERS,
  BulkDisconnectButton,
} from './accounts-table'
import { useOnboardingReturn } from '@/hooks/use-onboarding-return'
import { OAUTH_RETURN_TO } from '@/lib/onboarding'
import { ChannelSetupGuide } from './channel-setup-guide'
import { noConnectHint } from './channel-catalog'
import { useMyPermissions } from '@/hooks/use-settings'

/** Hiện khi backend chưa có khoá ứng dụng Meta. */
function FbSetupGuide({ cfg }: { cfg: FbConfigStatus }) {
  const { t } = useTranslation('chanSocial')
  return (
    <ChannelSetupGuide
      title={cfg.hostMismatch ? t('fb.setup.titleHostMismatch') : t('fb.setup.titleNotConfigured')}
      intro={
        cfg.hostMismatch
          ? t('fb.setup.introHostMismatch', { redirectUri: cfg.redirectUri, apiBase: cfg.apiBase })
          : t('fb.setup.introDefault', { graphVersion: cfg.graphVersion })
      }
      steps={[
        <Trans
          t={t}
          i18nKey="fb.setup.step1"
          components={{
            b: <strong />,
            link: <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="font-medium text-primary underline" />,
          }}
        />,
        <Trans t={t} i18nKey="fb.setup.step2" components={{ b: <strong />, em: <em /> }} />,
        <Trans
          t={t}
          i18nKey="fb.setup.step3"
          values={{ fields: cfg.webhookFields.join(', ') }}
          components={{ b: <strong />, code: <code className="rounded bg-background px-1" /> }}
        />,
        <Trans
          t={t}
          i18nKey="fb.setup.step4"
          values={{ scopes: cfg.scopes.join(', ') }}
          components={{ code: <code className="rounded bg-background px-1" /> }}
        />,
        <Trans t={t} i18nKey="fb.setup.step5" components={{ b: <strong />, code: <code className="rounded bg-background px-1" /> }} />,
      ]}
      copyItems={[
        { label: 'Valid OAuth Redirect URI', value: cfg.redirectUri },
        { label: 'Webhook URL (Callback URL)', value: cfg.webhookUrl },
        ...(cfg.suggestedVerifyToken
          ? [{ label: t('fb.setup.verifyTokenLabel'), value: cfg.suggestedVerifyToken }]
          : []),
      ]}
      missing={cfg.missing.map((k) =>
        k === 'FACEBOOK_WEBHOOK_VERIFY_TOKEN' && cfg.suggestedVerifyToken
          ? `${k}=${cfg.suggestedVerifyToken}`
          : `${k}=`,
      )}
      footnote={
        <Trans t={t} i18nKey="fb.setup.footnote" components={{ b: <strong />, code: <code className="rounded bg-background px-1" /> }} />
      }
    />
  )
}

/**
 * Kết luận vì sao Messenger không về, theo đúng thứ tự Meta xử lý sự kiện.
 *
 * Webhook Messenger phải khớp ĐỦ HAI CẤP mới có tin, mà thiếu cấp nào cũng im
 * lặng y hệt nhau — nên ở đây chỉ thẳng cấp nào đang hỏng thay vì báo chung
 * chung "chưa nhận được tin".
 */
function FbDiagnosisPanel({ d }: { d: FbDiagnosis }) {
  const { t } = useTranslation('chanSocial')
  const ket = d.appError
    ? {
        variant: 'warning' as const,
        title: t('fb.diag.appError.title'),
        desc: t('fb.diag.appError.desc', { error: d.appError }),
      }
    : d.luotGoiGanDay.length > 0 && d.luotGoiGanDay.every((h) => !h.signatureOk)
      ? {
          // Meta gửi tới nơi rồi mà mình từ chối — gần như luôn là App Secret
          // trong container khác với App Secret của app đang gửi.
          variant: 'destructive' as const,
          title: t('fb.diag.badSignature.title'),
          desc: t('fb.diag.badSignature.desc'),
        }
    : !d.appDaKhaiWebhook || !d.appCoTruongMessages
      ? {
          variant: 'destructive' as const,
          title: t('fb.diag.noWebhook.title'),
          desc: t('fb.diag.noWebhook.desc'),
        }
      : d.pageError
        ? {
            variant: 'warning' as const,
            title: t('fb.diag.pageError.title'),
            desc: t('fb.diag.pageError.desc', { error: d.pageError }),
          }
        : !d.pageDaDangKyApp || !d.pageCoTruongMessages
          ? {
              variant: 'destructive' as const,
              title: t('fb.diag.pageNotSubscribed.title'),
              desc: t('fb.diag.pageNotSubscribed.desc'),
            }
          : d.soTin > 0
            ? {
                variant: 'success' as const,
                title: t('fb.diag.received.title', { soTin: d.soTin, soHoiThoai: d.soHoiThoai }),
                desc: t('fb.diag.received.desc'),
              }
            : {
                variant: 'warning' as const,
                title: t('fb.diag.noMessages.title'),
                desc: t('fb.diag.noMessages.desc'),
              }

  const tone =
    ket.variant === 'destructive'
      ? 'border-destructive/40 bg-destructive/10'
      : ket.variant === 'success'
        ? 'border-success/40 bg-success/10'
        : 'border-warning/40 bg-warning/10'

  return (
    <div className={`mt-2 space-y-2 rounded-md border p-3 text-sm ${tone}`}>
      <p className="font-medium">{ket.title}</p>
      <p className="text-muted-foreground">{ket.desc}</p>
      <dl className="grid gap-x-3 gap-y-1 text-xs text-muted-foreground sm:grid-cols-[auto_1fr]">
        <dt>{t('fb.diag.calls.label')}</dt>
        <dd>
          {d.luotGoiGanDay.length === 0
            ? t('fb.diag.calls.none')
            : t('fb.diag.calls.summary', {
                count: d.luotGoiGanDay.length,
                valid: d.luotGoiGanDay.filter((h) => h.signatureOk).length,
                latest: dayjs(d.luotGoiGanDay[0].at).format('HH:mm DD/MM'),
              })}
        </dd>
        <dt>{t('fb.diag.appLevel')}</dt>
        <dd>{d.appDaKhaiWebhook ? d.appFields.join(', ') || t('fb.diag.noFields') : t('fb.diag.webhookNotDeclared')}</dd>
        <dt>{t('fb.diag.pageLevel')}</dt>
        <dd>{d.pageDaDangKyApp ? d.pageFields.join(', ') || t('fb.diag.noFields') : t('fb.diag.appNotRegistered')}</dd>
        {d.appCallbackUrl && (
          <>
            <dt>{t('fb.diag.callbackAtMeta')}</dt>
            <dd className="break-all font-mono">{d.appCallbackUrl}</dd>
          </>
        )}
        {d.appKhacDangNhan.length > 0 && (
          <>
            {/* Page dùng chung với Pancake/chatbot cũ là chuyện bình thường, nhưng
                biết trước thì đỡ hoảng khi thấy tin nhắn nhân đôi ở hai nơi. */}
            <dt>{t('fb.diag.otherApps')}</dt>
            <dd>{d.appKhacDangNhan.join(', ')}</dd>
          </>
        )}
        {d.tinGanNhat && (
          <>
            <dt>{t('fb.diag.latestMessage')}</dt>
            <dd>{dayjs(d.tinGanNhat).format('DD/MM/YYYY HH:mm')}</dd>
          </>
        )}
      </dl>
    </div>
  )
}

export function FacebookPageTab() {
  const { t } = useTranslation('chanSocial')
  // Không có endpoint riêng cho Facebook — dùng danh sách kênh tổng rồi lọc theo platform.
  const { data, isLoading, isError } = useZaloAccounts()
  const { data: cfg } = useFacebookConfig()
  const connect = useConnectFacebookPage()
  const remove = useDeleteZaloAccount()
  const diagnose = useDiagnoseFacebookPage()
  const [chanDoan, setChanDoan] = useState<Record<string, FbDiagnosis>>({})
  // Kết nối Fanpage = create, ngắt = delete; chẩn đoán chỉ cần quyền xem nên luôn hiện.
  const { data: perms } = useMyPermissions()
  const canCreate = !!perms?.has('integrations.create')
  const canDelete = !!perms?.has('integrations.delete')

  const pages = useMemo(
    () => (data ?? []).filter((a) => a.platform === PLATFORM.FACEBOOK_PAGE),
    [data],
  )

  // Mở từ trang Bắt đầu → callback OAuth đưa về /onboarding (returnTo lưu trong state phía server).
  const onboardingReturn = useOnboardingReturn()
  const handleConnect = () => {
    connect.mutate(onboardingReturn.active ? { returnTo: OAUTH_RETURN_TO } : undefined, {
      onSuccess: (url) => {
        window.location.href = url
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  // Cố ý KHÔNG tự chạy luồng uỷ quyền khi vào trang kèm ?add=1 (khác Zalo OA / Shopee): người dùng
  // phải thấy trang, xem hướng dẫn/cấu hình rồi tự bấm "Kết nối Fanpage" — đỡ bị văng sang Facebook bất ngờ.

  const handleDisconnect = (id: string) => {
    remove.mutate(id, {
      onSuccess: () => toast.success(t('fb.disconnected')),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('fb.intro')}
        </p>
        {canCreate && (
          <Button onClick={handleConnect} disabled={connect.isPending || cfg?.configured === false}>
            {connect.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
            {t('fb.connect')}
          </Button>
        )}
      </div>

      {cfg && !cfg.configured && <FbSetupGuide cfg={cfg} />}

      <SelectableTable
        rows={pages}
        rowKey={(a) => a.id}
        loading={isLoading}
        error={isError}
        filters={ACCOUNT_FILTERS}
        searchText={accountSearchText}
        empty={
          <EmptyState
            icon={Facebook}
            title={t('fb.empty.title')}
            description={
              canCreate ? t('fb.empty.description') : noConnectHint()
            }
          />
        }
        columns={accountColumns({
          fallbackName: 'Facebook Page',
          sub: (acc) => (acc.externalPageId ? `Page ID ${acc.externalPageId}` : undefined),
          actions: (acc) => (
            <>
              <Button
                variant="ghost"
                size="sm"
                title={t('fb.diagnoseTitle')}
                disabled={diagnose.isPending}
                onClick={() =>
                  diagnose.mutate(acc.id, {
                    onSuccess: (d) => setChanDoan((s) => ({ ...s, [acc.id]: d })),
                    onError: (e) => toast.error(apiError(e)),
                  })
                }
              >
                <Stethoscope />
              </Button>
              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDisconnect(acc.id)}
                  disabled={remove.isPending}
                  title={t('common:actions.disconnect')}
                >
                  <Unplug />
                </Button>
              )}
            </>
          ),
        })}
        renderDetail={(acc) => (chanDoan[acc.id] ? <FbDiagnosisPanel d={chanDoan[acc.id]} /> : null)}
        bulkActions={(selected, clear) => <BulkDisconnectButton selected={selected} onDone={clear} />}
      />
    </div>
  )
}
